/**
 * CountryDetailPanel · 单国家详情面板（贴 table 右侧）
 *
 * 参考 Figma 3272-47070 popover 样式：
 * - 头部：国家 flag + 国名 + 关闭按钮
 * - 表格化主体（用 TableCommonHook）：
 *   - 列 1: 序号 + 游戏（cover + 游戏名）
 *   - 列 2: Active Ads（数值；点击表头切排序 desc/asc）
 * - 数据来自父组件传入的 row（不再发额外请求）
 */
import React, { useMemo, useState } from 'react';
import TableCommonHook from '@op/components/TableCommonHook';
import RegionCountryItem from '@op/components/Snippets/RegionCountryItem';
import RingRatioBubble from '@op/components/Snippets/RingRatioBubble';
import PercentageItem from '@op/components/Snippets/PercentageItem';
import { DBO_TM, DBO_V } from '@op/utils/format';
import GameCell from '../KpiOverview/GameCell';
import useIntls from '@opm/hooks/useIntls';
import type { SortBy } from 'tea-component/lib/table/addons';
import { ICountryGameStat, ICountryRowItem } from '../../types/country';
import { TGameMap } from '../../hooks/useGameMap';
import './index.scss';

export interface ICountryDetailPanelProps {
  /** 当前国家行数据 */
  row: ICountryRowItem;
  gameMap: TGameMap;
  /** 关闭回调 */
  onClose: () => void;
  /** 父级提供的高度（与 table 一致） */
  height?: number;
  /** 当前周期时间窗口（RingRatioBubble Hover 上期对比气泡用） */
  newTime?: [string, string];
  /** 上一周期时间窗口（RingRatioBubble Hover 上期对比气泡用） */
  oldTime?: [string, string];
}

/** 表格行（带预计算 rank） */
interface IDetailRow extends ICountryGameStat {
  rank: number;
}

const ROW_HEIGHT = 52;
const HEADER_HEIGHT = 40;
/** 详情面板顶部头（国旗 + 标题 + 关闭按钮）高度 */
const PANEL_HEAD_HEIGHT = 49;
/** 表格最小可见高度 */
const TABLE_MIN_HEIGHT = 200;

export default function CountryDetailPanel({
  row,
  gameMap,
  onClose,
  height,
  newTime,
  oldTime,
}: ICountryDetailPanelProps) {
  const { intl } = useIntls();
  /** 表格排序 state（默认按 value desc） */
  const [sort, setSort] = useState<SortBy<any>>({ by: 'value', order: 'desc' });

  const labels = useMemo(
    () => ({
      detailTitle: intl('Active Ads Detail'),
      gameHeader: intl('Game'),
      valueHeader: intl('Active Ads'),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [intl],
  );

  /** 排序后 + 编号的行数据 */
  const records = useMemo<IDetailRow[]>(() => {
    const list = [...(row.games || [])];
    const dir = sort.order === 'asc' ? 1 : -1;
    list.sort((a, b) => (a.value - b.value) * dir);
    return list.map((g, idx) => ({ ...g, rank: idx + 1 }));
  }, [row.games, sort]);

  const columns = useMemo(
    () => [
      {
        key: 'rank_game',
        header: (
          <div className="dbom-country-detail-panel__header">
            #<span>{labels.gameHeader}</span>
          </div>
        ),
        render: (record: IDetailRow) => {
          const game = gameMap[record.game_id];
          return (
            <GameCell
              gameName={game?.game_name || record.game_id}
              cover={game?.cover}
              ranking={record.rank}
            />
          );
        },
        width: 280,
      },
      {
        key: 'value',
        header: labels.valueHeader,
        align: 'right' as const,
        width: 180,
        render: (record: IDetailRow) => {
          const { value, old_value: oldValueRaw } = record;
          const oldValue = oldValueRaw ?? 0;
          const qoq = DBO_V.qoq(value, oldValue, 6);
          return (
            <RingRatioBubble
              newTime={newTime ?? []}
              oldTime={oldTime ?? []}
              newValue={value}
              oldValue={oldValue}
              showContent={!!value}
              formateValue={(v) => DBO_V.reviews(Number(v))}
              formateDate={(date: string) => DBO_TM.sFormat(date)}
            >
              <span className="dbom-country-detail-panel__value-cell">
                <span className="dbom-country-detail-panel__value-text">
                  {value > 0 ? DBO_V.reviews(value) : '-'}
                </span>
                <PercentageItem percentage={qoq} />
              </span>
            </RingRatioBubble>
          );
        },
      },
    ],
    [labels, gameMap, newTime, oldTime],
  );

  /** TableCommonHook sortInfo 适配（仅 value 列可排） */
  const handleSortChange = (next: SortBy<any>) => {
    setSort(next?.by ? next : { by: 'value', order: 'desc' });
  };
  const sortInfo: [SortBy<any>, (s: SortBy<any>) => void] = [
    sort,
    handleSortChange,
  ];

  /** 表格内容自然高（用于 height prop）；过大时由 maxHeight 限制并产生滚动 */
  const naturalHeight = useMemo(
    () => Math.max(TABLE_MIN_HEIGHT, HEADER_HEIGHT + records.length * ROW_HEIGHT),
    [records.length],
  );

  /** 表格最大高（基于父级 panel 高度 - 头部高度），未传 height 时不限 */
  const maxTableHeight = useMemo(() => {
    if (!height) return undefined;
    return Math.max(TABLE_MIN_HEIGHT, height - PANEL_HEAD_HEIGHT);
  }, [height]);

  /** 实际给 table 的 height：min(natural, maxAllowed) → 内容多时启动 scroll */
  const tableHeight = useMemo(
    () =>
      maxTableHeight ? Math.min(naturalHeight, maxTableHeight) : naturalHeight,
    [naturalHeight, maxTableHeight],
  );

  return (
    <div
      className="dbom-country-detail-panel"
      style={height ? { height } : undefined}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="dbom-country-detail-panel__head">
        <div className="dbom-country-detail-panel__title">
          <RegionCountryItem
            market={row.country_code}
            iconStyle={{ width: 22, height: 16 }}
          />
          <span className="dbom-country-detail-panel__title-text">
            {labels.detailTitle}
          </span>
        </div>
        <span
          className="dbom-country-detail-panel__close"
          onClick={onClose}
          role="button"
          aria-label="close"
        >
          ✕
        </span>
      </div>

      <div className="dbom-country-detail-panel__body">
        <TableCommonHook
          className="dbom-country-detail-panel__table"
          records={records}
          columns={columns}
          recordKey={(r: IDetailRow) => r.game_id}
          sortColumns={['value']}
          sortInfo={sortInfo}
          sortType="server"
          hasPageable={false}
          height={tableHeight}
          // ⚠️ TableCommonHook 内部 scrollable({ maxHeight }) 处理表内滚动
          maxHeight={724}
          bordered={'all'}
          minWidth={460}
        />
      </div>
    </div>
  );
}
